import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { DOH_SERVER_OPTIONS } from '../../config';
import { useDeviceType } from '../../hooks/useDeviceType';

const DnsForm = ({ domain, setDomain, onLookup, loading, recordTypes, defaultEcsIp = '' }) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [recordType, setRecordType] = useState('A');
  const [dohServer, setDohServer] = useState('alibaba');
  const [ecsIp, setEcsIp] = useState(defaultEcsIp);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmedDomain = domain.trim();
    if (!trimmedDomain) return;
    // 使用 DOH (DNS over HTTPS) 查询方式
    onLookup(trimmedDomain, recordType, 'doh', dohServer, ecsIp.trim());
  };

  // 更新 ECS IP 地址当 defaultEcsIp 变化时
  useEffect(() => {
    setEcsIp(defaultEcsIp);
  }, [defaultEcsIp]);

  const { isMobile, isTablet } = useDeviceType();

  const formStyle = {
    background: colors.cardBackground,
    padding: isMobile ? '1.5rem' : '2rem',
    borderRadius: '16px',
    marginBottom: isMobile ? '1.5rem' : '2rem',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    backdropFilter: 'blur(10px)'
  };

  const titleStyle = {
    fontSize: isMobile ? '1rem' : '1.2rem',
    fontWeight: '600',
    color: colors.text,
    marginBottom: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  };

  const inputGroupStyle = {
    marginBottom: '1.5rem'
  };

  const labelStyle = {
    display: 'block',
    fontSize: isMobile ? '0.8rem' : '0.9rem',
    fontWeight: '600',
    color: colors.textSecondary,
    marginBottom: '0.5rem'
  };

  const inputStyle = {
    width: '100%',
    padding: isMobile ? '0.6rem 0.8rem' : '0.75rem 1rem',
    border: `2px solid ${colors.inputBorder}`,
    borderRadius: '8px',
    fontSize: isMobile ? '0.9rem' : '1rem',
    outline: 'none',
    transition: 'all 0.3s ease',
    fontFamily: 'Monaco, Consolas, monospace',
    boxSizing: 'border-box',
    background: colors.inputBackground,
    color: colors.text
  };

  const selectStyle = {
    ...inputStyle,
    fontFamily: 'inherit',
    cursor: 'pointer',
    colorScheme: colors.isDarkMode ? 'dark' : 'light'
  };

  const flexRowStyle = {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr',
    gap: isMobile ? '1rem' : '1rem',
    marginBottom: '1.5rem'
  };

  const buttonStyle = {
    width: '100%',
    padding: isMobile ? '0.8rem' : '1rem',
    background: loading 
      ? colors.border 
      : colors.primary,
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: isMobile ? '0.9rem' : '1rem',
    fontWeight: '600',
    cursor: loading ? 'not-allowed' : 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: loading ? 'none' : '0 4px 15px rgba(102, 126, 234, 0.3)'
  };

  const hintStyle = {
    fontSize: isMobile ? '0.7rem' : '0.8rem',
    color: colors.isDarkMode ? '#b0b0b0' : colors.textSecondary,
    marginTop: '0.5rem',
    padding: '0.5rem',
    background: colors.isDarkMode 
      ? 'rgba(102, 126, 234, 0.15)' 
      : 'rgba(102, 126, 234, 0.05)',
    border: `1px solid ${colors.isDarkMode ? 'rgba(102, 126, 234, 0.3)' : 'rgba(102, 126, 234, 0.1)'}`,
    borderRadius: '4px',
    lineHeight: '1.4'
  };

  // DOH (DNS over HTTPS) 服务器选项
  const dohServerOptions = DOH_SERVER_OPTIONS.map(option => ({
    value: option.value,
    label: t(option.labelKey)
  }));

  return (
    <div style={formStyle}>
      <h3 style={titleStyle}>
        {t('dns.formTitle')}
      </h3>

      <form onSubmit={handleSubmit}>
        <div style={flexRowStyle}>
          <div style={inputGroupStyle}>
            <label style={labelStyle}>{t('dns.fields.domain')}</label>
            <input
              type="text"
              placeholder={t('dns.placeholders.domain')}
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              style={inputStyle}
              required
            />
            <div style={hintStyle}>
              {t('dns.hints.domain')}
            </div>
          </div>

          <div style={inputGroupStyle}>
            <label style={labelStyle}>{t('dns.fields.recordType')}</label>
            <select
              value={recordType}
              onChange={(e) => setRecordType(e.target.value)}
              style={selectStyle}
            >
              {recordTypes.map(type => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* DOH (DNS over HTTPS) 服务器选择 */}
        <div style={inputGroupStyle}>
          <label style={labelStyle}>{t('dns.fields.dohServer')}</label>
          <select
            value={dohServer}
            onChange={(e) => setDohServer(e.target.value)}
            style={selectStyle}
          >
            {dohServerOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* ECS IP 地址输入框 */}
        <div style={inputGroupStyle}>
          <label style={labelStyle}>{t('dns.fields.ecsIp')}</label>
          <input
            type="text"
            placeholder="例如: 192.168.1.1"
            value={ecsIp}
            onChange={(e) => setEcsIp(e.target.value)}
            style={inputStyle}
          />
          <div style={hintStyle}>
            {t('dns.hints.ecsIp')}
          </div>
        </div>

        <button type="submit" disabled={loading} style={buttonStyle}>
          {loading ? t('dns.searching') : t('dns.searchButton')}
        </button>
      </form>

      <div style={{
        marginTop: '1rem',
        padding: '1rem',
        background: colors.isDarkMode 
          ? 'rgba(76, 175, 80, 0.15)' 
          : 'rgba(76, 175, 80, 0.08)',
        border: `1px solid ${colors.isDarkMode ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.2)'}`,
        borderRadius: '8px',
        fontSize: isMobile ? '0.75rem' : '0.85rem',
        color: colors.isDarkMode ? '#a5d6a7' : '#2e7d32'
      }}>
        <strong>{t('dns.recordTypes.title')}</strong>
        <div style={{ marginTop: '0.5rem', lineHeight: '1.5' }}>
          {t('dns.recordTypes.descriptions')}
        </div>
      </div>
    </div>
  );
};

export default DnsForm;
